import {
  moveOutOfMatrix,
  shouldMoveToMatrix,
  shouldMoveOutOfMatrix,
  moveToMatrix,
} from "@/components/expression/utils/matrix";
import { moveToNextGraphExpression, moveToPrevGraphExpression } from "@/components/graph/utils";
import { trackedHintType } from "@/components/suggestions/types";
import { type KeyboardInputAction } from "@/input/types";
import { autoEditNode } from "@/input/utils/auto-edit/autoEditNode";
import { acceptPhantomHint, acceptTableRowHints } from "@/input/utils/editOperators";
import {
  openGroupDecreaseLeft,
  openGroupDecreaseRight,
  openGroupIncreaseLeft,
  openGroupIncreaseRight,
  shouldOpenGroupDecreaseLeft,
  shouldOpenGroupDecreaseRight,
  shouldOpenGroupIncreaseLeft,
  shouldOpenGroupIncreaseRight,
} from "@/input/utils/openGroup";
import { GraphExpressionNodeModel } from "@/models/nodes/GraphExpressionNodeModel";

import { enterAdjacentLabel } from "../utils/labels";

import type { DocumentModel } from "@/models/DocumentModel";
import type { KeyCombination, Symbol } from "@corca/data";

export const movementShortcuts = {
  ArrowLeft: {
    action: (doc: DocumentModel) => {
      const { from, range } = doc.selection;
      if (doc.input.editingNode && doc.input.inputSelection) {
        const { inputSelection } = doc.input;
        if (inputSelection.from !== inputSelection.to) return false;
      }

      doc.transact(() => {
        if (shouldOpenGroupIncreaseLeft(doc)) {
          openGroupIncreaseLeft(doc);
        } else if (shouldOpenGroupDecreaseRight(doc)) {
          openGroupDecreaseRight(doc);
        } else if (range && from !== undefined) {
          doc.cursor.moveTo(range.node, from);
        } else if (shouldMoveToMatrix(doc.cursor, "left")) {
          moveToMatrix(doc.cursor, "left");
        } else if (shouldMoveOutOfMatrix(doc.cursor, "left")) {
          moveOutOfMatrix(doc.cursor, "left");
        } else if (!autoEditNode(doc, "left")) {
          doc.editor.resetInput(true);
          doc.cursor.moveLeft();
        }
      }, "shortcut:ArrowLeft");

      doc.selection.resetSelection();
      return true;
    },
  },
  ArrowRight: {
    action: (doc: DocumentModel) => {
      const { range, to } = doc.selection;
      if (doc.input.editingNode && doc.input.inputSelection) {
        const { inputSelection } = doc.input;
        if (inputSelection.from !== inputSelection.to) return false;
      }

      doc.transact(() => {
        if (shouldOpenGroupIncreaseRight(doc)) {
          openGroupIncreaseRight(doc);
        } else if (shouldOpenGroupDecreaseLeft(doc)) {
          openGroupDecreaseLeft(doc);
        } else if (range && to !== undefined) {
          doc.cursor.moveTo(range.node, to);
        } else if (shouldMoveToMatrix(doc.cursor, "right")) {
          moveToMatrix(doc.cursor, "right");
        } else if (shouldMoveOutOfMatrix(doc.cursor, "right")) {
          moveOutOfMatrix(doc.cursor, "right");
        } else if (doc.cursor.isDefaultCursorContext && doc.cursor.isAtTheEndOf("label")) {
          doc.cursor.moveToLevel(doc.cursor.level - 1);
        } else if (!autoEditNode(doc, "right")) {
          doc.editor.resetInput(true);
          doc.cursor.moveRight();
        }
      }, "shortcut:ArrowRight");

      doc.selection.resetSelection();
      return true;
    },
  },
  ArrowUp: {
    action: (doc: DocumentModel) => {
      if (doc.editor.suggestions.visible) {
        return false;
      }

      if (!enterAdjacentLabel(doc, "right-top")) {
        doc.cursor.moveUp();
      }

      doc.selection.resetSelection();
      return true;
    },
  },
  ArrowDown: {
    action: (doc: DocumentModel) => {
      if (doc.editor.suggestions.visible) {
        return false;
      }

      if (!enterAdjacentLabel(doc, "right-bottom")) {
        doc.cursor.moveDown();
      }

      doc.selection.resetSelection();
      return true;
    },
  },
  "Alt+ArrowLeft": {
    action: (doc: DocumentModel) => {
      doc.editor.resetInput(true);
      doc.cursor.moveToPrevSibling();
      doc.selection.resetSelection();
      return true;
    },
  },
  "Alt+ArrowRight": {
    action: (doc: DocumentModel) => {
      doc.editor.resetInput(true);
      doc.cursor.moveToNextSibling();
      doc.selection.resetSelection();
      return true;
    },
  },
  // TODO: Alt+ArrowUp/Down in most editors moves the whole line up or down
  // "Alt+ArrowUp": {
  // },
  // "Alt+ArrowDown": {
  // },
  "Mod+ArrowLeft": {
    action: (doc: DocumentModel) => {
      doc.editor.resetInput(true);
      doc.cursor.moveToLineStart();
      doc.selection.resetSelection();
      return true;
    },
  },
  "Mod+ArrowRight": {
    action: (doc: DocumentModel) => {
      doc.editor.resetInput(true);
      doc.selection.resetSelection();
      doc.cursor.moveToLineEnd();
      doc.selection.resetSelection();
      return true;
    },
  },
  Tab: {
    action: (doc: DocumentModel, { input }) => {
      const { node } = doc.cursor;
      const suggestionsShown = !!input && doc.editor.suggestions.mode === "active";
      if (!suggestionsShown && doc.editor.hints.hasTableRowHints) {
        acceptTableRowHints(doc);
        return true;
      }
      const { phantomHintResult, phantomHint } = doc.editor.hints;
      if (!suggestionsShown && phantomHintResult) {
        acceptPhantomHint(doc, phantomHintResult.children, trackedHintType(phantomHint));
        return true;
      }
      doc.editor.resetInput(true);

      if (node?.parent?.is(GraphExpressionNodeModel)) {
        moveToNextGraphExpression(node.parent);
      } else {
        doc.cursor.moveRight();
      }

      return true;
    },
  },
  "Shift+Tab": {
    action: (doc: DocumentModel) => {
      const { node } = doc.cursor;

      if (doc.editor.hints.phantomHintSize > 0) {
        doc.editor.hints.findNextPhantomHintIndex();
        return true;
      }
      doc.editor.resetInput(true);

      if (node?.parent?.is(GraphExpressionNodeModel)) {
        moveToPrevGraphExpression(node.parent);
      } else {
        doc.cursor.moveLeft();
      }

      return true;
    },
  },
  Home: {
    action: (doc: DocumentModel) => {
      doc.editor.resetInput(true);
      doc.cursor.moveToLineStart();
      return true;
    },
  },
  End: {
    action: (doc: DocumentModel) => {
      doc.editor.resetInput(true);
      doc.cursor.moveToLineEnd();
      return true;
    },
  },
} satisfies Partial<Record<KeyCombination | Symbol, KeyboardInputAction>>;
